import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Container } from '../ui/Container';
import { Button } from '../ui/Button';
import { ChevronDown, Phone, Shield, DollarSign, Users } from 'lucide-react';
function SimpleAccordion({
  items
}: {
  items: {
    question: string;
    answer: string;
  }[];
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  return <div className="space-y-3 h-full flex flex-col" data-id="element-102">
      {items.map((item, index) => <motion.div key={index} className="bg-dashboard-surface rounded-lg border border-dashboard-border flex-1" initial={{
      opacity: 0,
      y: 10
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      delay: index * 0.05,
      duration: 0.3
    }} data-id="element-103">
          <button onClick={() => setOpenIndex(openIndex === index ? null : index)} className="flex items-center justify-between w-full p-5 text-left hover:bg-dashboard-bg/50 transition-colors rounded-lg" data-id="element-104">
            <span className="font-bold text-dashboard-text pr-4 text-sm" data-id="element-105">
              {item.question}
            </span>
            <ChevronDown className={`h-4 w-4 text-tech-blue transition-transform flex-shrink-0 ${openIndex === index ? 'rotate-180' : ''}`} data-id="element-106" />
          </button>
          {openIndex === index && <div className="px-5 pb-5 text-dashboard-muted leading-relaxed border-t border-dashboard-border" data-id="element-107">
              <div className="pt-4 text-sm" data-id="element-108">{item.answer}</div>
            </div>}
        </motion.div>)}
    </div>;
}
export function FAQ() {
  // Surface top 2 critical objections
  const criticalObjections = [{
    icon: DollarSign,
    question: 'How much does this cost?',
    answer: 'Most clients see ROI within 3-6 months through time savings alone. Typical engagement: $15-50K depending on scope.'
  }, {
    icon: Users,
    question: 'What if our IT team is sceptical?',
    answer: 'We work WITH your IT team, not around them. Everything follows your governance policies and security standards.'
  }];
  const faqs = [{
    question: 'Do we need to buy new software?',
    answer: "No. We optimise the Microsoft 365 ecosystem you already own (SharePoint, Teams, Power Automate). You're already paying for these tools—we just make them work properly."
  }, {
    question: 'How long does implementation take?',
    answer: "Diagnosis: 1-2 weeks. Implementation: 4-12 weeks depending on scope. You'll see results within the first month."
  }, {
    question: 'Is my data secure?',
    answer: 'Yes. We work inside your existing Microsoft tenant. Your data never leaves your environment. No external systems, no data migration, no security risks.'
  }, {
    question: 'Do you support us after the build?',
    answer: "Absolutely. We offer ongoing support and training packages to ensure adoption. We don't disappear after implementation."
  }];
  return <section className="relative overflow-hidden bg-dashboard-bg py-16 md:py-24 border-y border-dashboard-border" data-id="element-109">
      <Container data-id="element-110">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12" data-id="element-111">
          <motion.div className="lg:col-span-5 flex flex-col" initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-112">
            <div className="mb-6" data-id="element-113">
              <span className="text-xs text-tech-blue uppercase tracking-wider" data-id="element-114">
                // Objection Handling
              </span>
            </div>
            <h2 className="text-4xl font-black tracking-tight text-dashboard-text sm:text-5xl mb-6" data-id="element-115">
              Questions <span className="text-tech-blue" data-id="element-116">Answered</span>
            </h2>
            <p className="text-lg text-dashboard-muted mb-10 leading-relaxed" data-id="element-117">
              We know you're sceptical of consultants. Here's the straight talk
              on how we operate—no fluff, no sales speak.
            </p>

            {/* Critical objections surfaced - equal height boxes */}
            <div className="space-y-4 mb-8 flex-1 flex flex-col" data-id="element-118">
              {criticalObjections.map((objection, index) => <motion.div key={index} className="bg-dashboard-surface rounded-lg p-6 border border-dashboard-border flex-1" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1,
              duration: 0.5
            }} data-id="element-119">
                  <div className="flex items-start gap-3" data-id="element-120">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-tech-blue/10 border border-tech-blue/30 flex items-center justify-center" data-id="element-121">
                      <objection.icon className="w-5 h-5 text-tech-blue" data-id="element-122" />
                    </div>
                    <div className="flex-1" data-id="element-123">
                      <p className="text-sm font-bold text-dashboard-text mb-2" data-id="element-124">
                        {objection.question}
                      </p>
                      <p className="text-xs text-dashboard-muted leading-relaxed" data-id="element-125">
                        {objection.answer}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>

            {/* Trust badge */}
            <div className="mb-8 bg-tech-green/5 rounded-lg p-6 border border-tech-green/30" data-id="element-126">
              <div className="flex items-center gap-3 mb-3" data-id="element-127">
                <div className="h-10 w-10 rounded-lg bg-tech-green/10 flex items-center justify-center" data-id="element-128">
                  <Shield className="h-5 w-5 text-tech-green" data-id="element-129" />
                </div>
                <div data-id="element-130">
                  <p className="font-bold text-dashboard-text text-sm" data-id="element-131">
                    100% Secure
                  </p>
                  <p className="text-xs text-dashboard-muted" data-id="element-132">
                    Works inside your Microsoft tenant
                  </p>
                </div>
              </div>
              <p className="text-xs text-dashboard-muted leading-relaxed" data-id="element-133">
                Your data never leaves your environment. We follow your IT
                governance and security policies.
              </p>
            </div>

            <div className="bg-tech-blue/5 rounded-lg p-6 border border-tech-blue/30" data-id="element-134">
              <h3 className="font-bold text-dashboard-text text-sm mb-3" data-id="element-135">
                Still have questions?
              </h3>
              <p className="text-dashboard-muted mb-6 text-xs leading-relaxed" data-id="element-136">
                Book a 15-minute call. No pressure, no sales pitch—just honest
                answers.
              </p>
              <Button variant="cta" className="w-full group" data-id="element-137">
                <Phone className="mr-2 h-4 w-4" data-id="element-138" />
                Book a Quick Call
              </Button>
            </div>
          </motion.div>
          <div className="lg:col-span-7 flex flex-col" data-id="element-139">
            <SimpleAccordion items={faqs} data-id="element-140" />
          </div>
        </div>
      </Container>
    </section>;
}